import { ref } from 'vue'
import { dashboardApi } from '@/api/dashboard'
import { githubApi } from '@/api/github'
import { apiErrorMessage } from '@/lib/errors'
import { withAsyncState } from '@/lib/asyncState'
import type { DashboardActivityEntry, DashboardOverview } from '@/types/models'

const REVIEW_RUN_ID_PREFIX = 'review_run:'

// Singleton, following useQueueMonitor.ts's shape: the last overview stays on
// screen while a refetch is in flight, so refreshing doesn't blank the page.
const overview = ref<DashboardOverview | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)

export function useDashboard() {
  async function fetchOverview(): Promise<void> {
    await withAsyncState(
      error,
      'Failed to load dashboard.',
      async () => {
        overview.value = await dashboardApi.overview()
      },
      loading,
    )
  }

  // Called on logout so the next session's dashboard doesn't briefly show the
  // previous user's data before its own fetch resolves.
  function reset(): void {
    overview.value = null
    loading.value = false
    error.value = null
  }

  // Mutates the shared overview in place rather than refetching the whole
  // dashboard: the feed is billed as live, and a refetch would both cost an
  // extra aggregate query and risk racing the 30s poll. Callers (the feed
  // item's own cancel button) own the request's loading/error UI; this just
  // makes a successful cancel visible without a reload. Throws on failure so
  // the caller can show its own inline error.
  async function cancelActivityEntry(entry: DashboardActivityEntry): Promise<void> {
    // Only review_run entries carry an installation id — the UI already gates
    // the cancel button on that, but this guards any other caller too.
    if (!entry.github_installation_id) return

    const reviewRunId = entry.id.replace(REVIEW_RUN_ID_PREFIX, '')
    try {
      const updated = await githubApi.cancelReviewRun(entry.github_installation_id, reviewRunId)
      const target = overview.value?.activity.find((item) => item.id === entry.id)
      if (target) target.status = updated.status
    } catch (e: unknown) {
      throw new Error(apiErrorMessage(e, 'Failed to cancel the review run.'))
    }
  }

  return {
    overview,
    loading,
    error,
    fetchOverview,
    cancelActivityEntry,
    reset,
  }
}
